import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  BookOpen, 
  Sun, 
  Moon, 
  LogOut, 
  ArrowLeft,
  Flame,
  Trophy,
  Target,
  Star,
  Crown,
  Award,
  Bookmark,
  Map,
  Library,
  Settings,
  MessageSquare
} from "lucide-react";
import { KITT } from "@/constants/hedgekit";

const ICON_MAP = {
  "book-open": BookOpen,
  "flame": Flame,
  "trophy": Trophy,
  "crown": Crown,
  "star": Star,
  "bookmark": Bookmark,
  "map": Map,
  "library": Library,
  "target": Target,
  "award": Award,
};

export default function Stats() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState(20);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/gamification/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        setNewGoal(data.daily_goal?.daily_sentences_goal || 20);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveGoal = async () => {
    try {
      const response = await fetch(`${API}/gamification/set-goal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ daily_sentences_goal: newGoal })
      });
      if (response.ok) {
        toast.success(`Daily goal set to ${newGoal} sentences!`);
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to save goal:', error);
    }
    setShowGoalDialog(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <img 
            src={KITT.reading} 
            alt="Kitt reading" 
            className="w-32 h-32 object-contain mx-auto mb-4 animate-pulse"
          />
          <p className="text-muted-foreground">Loading your progress...</p>
        </div>
      </div>
    );
  }

  const streak = stats?.streak || {};
  const goal = stats?.daily_goal || {};
  const achievements = stats?.achievements || [];
  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const lockedAchievements = achievements.filter(a => !a.unlocked);
  
  const goalProgress = goal.daily_sentences_goal > 0 
    ? Math.min(100, (goal.today_sentences / goal.daily_sentences_goal) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/home')}
              className="rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="font-heading font-bold text-xl">My Progress</span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full p-1">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Streak Card */}
        <div className="bg-gradient-to-br from-orange-100 to-amber-50 dark:from-orange-950/50 dark:to-amber-950/30 rounded-3xl p-6 mb-6 animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-orange-500 rounded-2xl flex items-center justify-center">
                <Flame className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Streak</p>
                <p className="text-4xl font-heading font-black text-orange-600 dark:text-orange-400">
                  {streak.current_streak || 0} <span className="text-lg">days</span>
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Longest</p>
              <p className="text-2xl font-bold text-foreground">{streak.longest_streak || 0} days</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-orange-200 dark:border-orange-900/50">
            <div className="text-center">
              <p className="text-2xl font-bold">{streak.total_reading_days || 0}</p>
              <p className="text-xs text-muted-foreground">Total Days</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{streak.total_sentences || 0}</p>
              <p className="text-xs text-muted-foreground">Sentences Read</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{streak.books_completed || 0}</p>
              <p className="text-xs text-muted-foreground">Books Done</p>
            </div>
          </div>
        </div>

        {/* Mode Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6 animate-slide-up" style={{ animationDelay: '0.05s' }}>
          <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-2xl p-4 text-center">
            <MessageSquare className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700 dark:text-green-300">{streak.together_sentences || 0}</p>
            <p className="text-xs text-muted-foreground">Read With Me</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 text-center">
            <BookOpen className="w-6 h-6 text-amber-600 dark:text-amber-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{streak.student_sentences || 0}</p>
            <p className="text-xs text-muted-foreground">I'll Read</p>
          </div>
        </div>

        {/* Daily Goal Card */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-heading font-bold">Today's Goal</p>
                <p className="text-sm text-muted-foreground">
                  {goal.today_sentences || 0} / {goal.daily_sentences_goal || 20} sentences
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowGoalDialog(true)}
              className="rounded-full"
              data-testid="edit-goal-button"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
          <Progress value={goalProgress} className="h-3 rounded-full" />
          {goal.goal_met_today && (
            <p className="text-green-600 dark:text-green-400 text-sm font-medium mt-2 flex items-center gap-1">
              <Star className="w-4 h-4" /> Goal achieved! Great job!
            </p>
          )}
        </div>

        {/* Achievements */}
        <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <img 
                src={KITT.celebrating} 
                alt="Kitt" 
                className="w-12 h-12 object-contain"
              />
              <h2 className="text-xl font-heading font-bold">Achievements</h2>
            </div>
            <span className="text-sm text-muted-foreground">
              {stats?.unlocked_count || 0} / {stats?.total_achievements || 0}
            </span>
          </div>

          {/* Unlocked */}
          {unlockedAchievements.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-muted-foreground mb-3">Unlocked</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {unlockedAchievements.map((ach) => {
                  const Icon = ICON_MAP[ach.icon] || Star;
                  return (
                    <div
                      key={ach.id}
                      className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-center"
                      data-testid={`achievement-${ach.id}`}
                    >
                      <div className="w-12 h-12 bg-amber-400 dark:bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="font-heading font-bold text-sm">{ach.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{ach.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Locked */}
          {lockedAchievements.length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">Keep Going!</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {lockedAchievements.map((ach) => {
                  const Icon = ICON_MAP[ach.icon] || Star;
                  return (
                    <div
                      key={ach.id}
                      className="bg-muted/50 border border-border rounded-xl p-4 text-center opacity-60"
                      data-testid={`achievement-locked-${ach.id}`}
                    >
                      <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <p className="font-heading font-bold text-sm">{ach.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{ach.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer with affiliate link */}
        <footer className="mt-12 mb-20 sm:mb-8 text-center">
          <a
            href="https://get.emergent.sh/xjnotg1y8n0g"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 text-sm font-medium text-purple-600 dark:text-purple-400 hover:from-purple-500/20 hover:to-blue-500/20 transition-all"
          >
            <span>✨</span>
            Build Your Own App with Emergent
          </a>
          <p className="mt-2 text-[10px] text-muted-foreground/40">
            This site contains affiliate links. We may earn a commission at no extra cost to you.
          </p>
        </footer>
      </main>

      {/* Goal Setting Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Set Daily Goal</DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <p className="text-center text-4xl font-heading font-bold mb-4">
              {newGoal} <span className="text-lg text-muted-foreground">sentences</span>
            </p>
            <Slider
              value={[newGoal]}
              onValueChange={([v]) => setNewGoal(v)}
              min={5}
              max={100}
              step={5}
              className="mb-4"
            />
            <p className="text-sm text-muted-foreground text-center">
              How many sentences do you want to read each day?
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowGoalDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveGoal} data-testid="save-goal-button">
              Save Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
